import type { Navigator, Screen } from "../app/screen";
import { Api } from "../net/api";
import { pageShell, note } from "../ui/page";
import { choice, setChoice } from "../app/session";
import { audio } from "../fx/audio";

/**
 * DUELS - play a friend.
 *
 * ⭐ **THE ROOM CODE IS THE FEATURE.** Everything else here is plumbing around one thing: a short
 * code your friend can type. The server mints it (`roomCode: "S3HKPJ"`), so this screen shows it
 * large, offers a link that carries it (`#/duels?room=CODE`), and accepts one typed in.
 *
 * ⚠️ **THE SHAPE WAS READ OFF THE WIRE, NOT GUESSED** - the project's own rule. A created room is:
 *
 *     { id, name, isPrivate, matchMode, players: [{userId, userName, deckId, mmr}],
 *       hostId, roomCode, password, isLockedByPassword, createdOn, startedDateTime, closedDateTime }
 *
 * ⚠️ **ONLY THE HOST CAN START, AND THE SERVER DECIDES THAT** - `DuelRegistry.Start` returns an
 * error STRING rather than throwing, so a refusal has to be shown. A start button that silently
 * does nothing is the exact failure this rebuild keeps finding.
 *
 * ⚠️ **THE POLL IS OWNED AND STOPPED.** A timer that outlives its screen keeps hitting the server
 * from the menu, and the leak is invisible until the tab has been open an hour.
 */

interface RoomPlayer {
  userId?: string;
  userName?: string;
  deckId?: string | null;
}

interface Room {
  id?: string;
  name?: string;
  roomCode?: string;
  hostId?: string;
  players?: RoomPlayer[];
  startedDateTime?: string | null;
}

const POLL_MS = 2500;

let poll: number | null = null;

export const DuelsScreen: Screen = {
  id: "duels",

  mustOffer: ["BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const shell = pageShell(nav, "DUELS", "play a friend");
    host.append(shell.el);

    const root = document.createElement("div");
    root.className = "duel";
    shell.body.append(root);
    root.append(note("Looking for your room..."));

    // ⭐ `#/duels?room=CODE` - an invite is a ROUTE, not a special case. The router keeps the query.
    const invited = new URLSearchParams(location.hash.split("?")[1] ?? "").get("room");

    let me = "";
    try {
      const ud = await Api.userData() as { id?: string };
      me = String(ud?.id ?? "");
    } catch {
      // Not fatal: without it we cannot tell host from guest, so START is left to the server to
      // refuse - which it does, with a message.
    }

    async function refresh(): Promise<void> {
      let room: Room | null = null;
      try {
        room = await Api.duelActiveRoom() as Room | null;
      } catch (e) {
        root.replaceChildren(note(`Could not reach the duel lobby: ${describe(e)}`, true));
        return;
      }

      if (room && room.id) {
        // ⭐ The server started the match: leave for the board rather than sitting in a dead room.
        if (room.startedDateTime) {
          stopPoll();
          audio.play("opponentFound");
          nav.go("match");
          return;
        }
        drawRoom(room);
      } else {
        await drawLobby();
      }
    }

    function drawRoom(room: Room): void {
      root.replaceChildren();

      const head = document.createElement("div");
      head.className = "duel__roomhead";
      const title = document.createElement("h2");
      title.className = "duel__roomname";
      title.textContent = String(room.name ?? "Your room");
      head.append(title);

      // ⭐ THE CODE, LARGE. It is the only thing on this screen a player has to read out loud.
      const code = document.createElement("div");
      code.className = "duel__code";
      code.textContent = String(room.roomCode ?? "------");
      code.title = "Your friend types this to join";

      const hint = document.createElement("p");
      hint.className = "duel__hint";
      hint.textContent = "Give this code to your friend, or send them the link.";

      const copy = document.createElement("button");
      copy.type = "button";
      copy.className = "utilbtn";
      copy.dataset["label"] = "COPY LINK";
      copy.textContent = "COPY LINK";
      copy.addEventListener("click", async () => {
        const url = `${location.origin}${location.pathname}#/duels?room=${room.roomCode ?? ""}`;
        try {
          await navigator.clipboard.writeText(url);
          copy.textContent = "COPIED";
          // ⚠️ Says what happened, then returns - a button stuck on "COPIED" reads as broken.
          window.setTimeout(() => { copy.textContent = "COPY LINK"; }, 1600);
        } catch {
          // Clipboard permission can be refused; show the link so it can be copied by hand.
          hint.textContent = url;
        }
      });

      const seats = document.createElement("div");
      seats.className = "duel__seats";
      const players = Array.isArray(room.players) ? room.players : [];
      for (let i = 0; i < 2; i++) {
        const p = players[i];
        const seat = document.createElement("div");
        seat.className = "duel__seat" + (p ? "" : " duel__seat--empty");
        const nm = document.createElement("span");
        nm.className = "duel__seatname";
        // ⚠️ textContent - another player's name is untrusted text.
        nm.textContent = p ? String(p.userName ?? "Player") : "Waiting for a friend...";
        seat.append(nm);
        if (p && p.userId === room.hostId) {
          const tag = document.createElement("span");
          tag.className = "duel__hosttag";
          tag.textContent = "HOST";
          seat.append(tag);
        }
        /**
         * ⭐ **REMOVE, ON THE GUEST'S SEAT, FOR THE HOST ONLY.**
         *
         * 🚨 The server has implemented `DuelKickPlayer` all along - `DuelRegistry.Kick`, signalling
         * `PlayerLeft` to the room - and **no screen ever called it**. Found 2026-08-22 by
         * `verify_route_coverage.py`, a gate stranded in a phase nothing ran since the Unity client
         * was retired. A host who let the wrong person in had no way to undo it.
         *
         * ⚠️ It sits on the SEAT, not in the action row. A destructive control belongs next to the
         * thing it destroys; in a row beside START and LEAVE it is one mis-click from ending your
         * own session, and it would read as a third equal option rather than as a correction.
         */
        if (p && p.userId !== room.hostId && !!me && room.hostId === me) {
          const kick = document.createElement("button");
          kick.type = "button";
          kick.className = "duel__kick";
          kick.textContent = "REMOVE";
          kick.title = `Remove ${String(p.userName ?? "this player")} from your room`;
          kick.addEventListener("click", async () => {
            kick.disabled = true;
            try {
              await Api.duelKickPlayer();
              audio.play("click");
            } catch (e) {
              // ⛔ The server refuses with a STRING ("You are not hosting a room."). Show it.
              issues.replaceChildren(note(`Cannot remove: ${describe(e)}`, true));
            }
            await refresh();
          });
          seat.append(kick);
        }
        seats.append(seat);
      }

      const issues = document.createElement("div");
      issues.className = "duel__issues";

      const actions = document.createElement("div");
      actions.className = "duel__actions";

      const isHost = !!me && room.hostId === me;
      const ready = players.length >= 2;

      const start = plate("START", async () => {
        try {
          await Api.duelStartSession();
          audio.play("click");
        } catch (e) {
          // ⛔ The server refuses with a STRING. Showing it is the whole point.
          issues.replaceChildren(note(`Cannot start: ${describe(e)}`, true));
        }
      });
      start.disabled = !isHost || !ready;
      start.classList.toggle("plate--disabled", start.disabled);

      const leave = plate("LEAVE ROOM", async () => {
        try {
          await Api.duelLeaveRoom();
        } catch { /* leaving a room that is already gone is not an error worth showing */ }
        await refresh();
      }, true);

      actions.append(leave, start);

      if (!isHost) {
        issues.append(note("Only the host can start the duel."));
      } else if (!ready) {
        issues.append(note("Waiting for your friend to join."));
      }

      root.append(head, code, hint, copy, seats, issues, actions);
    }

    async function drawLobby(): Promise<void> {
      root.replaceChildren();

      const actions = document.createElement("div");
      actions.className = "duel__actions";
      actions.append(plate("CREATE ROOM", async () => {
        try {
          await Api.duelCreateRoom();
          audio.play("click");
          await refresh();
        } catch (e) {
          root.append(note(`Could not create a room: ${describe(e)}`, true));
        }
      }));

      // ── join by code ─────────────────────────────────────────────────────────────────────────
      const joinRow = document.createElement("div");
      joinRow.className = "duel__join";
      const input = document.createElement("input");
      input.className = "duel__codein";
      input.placeholder = "ROOM CODE";
      input.maxLength = 12;
      input.setAttribute("aria-label", "Room code");
      if (invited) input.value = invited;

      const joinBtn = plate("JOIN", async () => {
        const wanted = input.value.trim().toUpperCase();
        if (!wanted) return;
        await joinByCode(wanted);
      });
      joinRow.append(input, joinBtn);

      const listHead = document.createElement("h2");
      listHead.className = "duel__head";
      listHead.textContent = "OPEN ROOMS";
      const list = document.createElement("div");
      list.className = "duel__rooms";

      root.append(actions, joinRow, listHead, list);

      let rooms: Room[] = [];
      try {
        rooms = await Api.duelRooms() as Room[];
      } catch (e) {
        list.append(note(`Could not list rooms: ${describe(e)}`, true));
        return;
      }

      if (!rooms.length) {
        list.append(note("No open rooms. Create one and send the code."));
      }
      for (const r of rooms) {
        if (!r?.id) continue;
        const row = document.createElement("button");
        row.type = "button";
        row.className = "duel__room";
        row.dataset["label"] = String(r.name ?? "Room");
        const nm = document.createElement("span");
        nm.textContent = String(r.name ?? "Room");
        const seatsText = document.createElement("span");
        seatsText.className = "duel__roomseats";
        seatsText.textContent = `${(r.players ?? []).length}/2`;
        row.append(nm, seatsText);
        row.addEventListener("click", async () => {
          try {
            await Api.duelJoinRoom(String(r.id));
            audio.play("click");
            await refresh();
          } catch (e) {
            list.append(note(`Could not join: ${describe(e)}`, true));
          }
        });
        list.append(row);
      }

      // An invite link joins as soon as the list is known, without a second click.
      if (invited) await joinByCode(invited, rooms);
    }

    async function joinByCode(code: string, known?: Room[]): Promise<void> {
      let rooms = known;
      if (!rooms) {
        try {
          rooms = await Api.duelRooms() as Room[];
        } catch {
          return;
        }
      }
      const found = rooms.find((r) => String(r.roomCode ?? "").toUpperCase() === code);
      if (!found?.id) {
        root.append(note(`No open room with the code ${code}.`, true));
        return;
      }
      try {
        await Api.duelJoinRoom(String(found.id));
        await refresh();
      } catch (e) {
        root.append(note(`Could not join: ${describe(e)}`, true));
      }
    }

    // ⚠️ A deck must be chosen before a duel starts, or the server falls back to Decks[0] - the
    // same silent default that made every solo match play the same deck.
    if (!choice().deckId) {
      try {
        const decks = await Api.decks() as Array<{ id?: string; name?: string }>;
        const first = decks[0];
        if (first?.id) setChoice({ deckId: first.id, deckName: first.name ?? "" });
      } catch { /* the PLAY screen is the proper place to choose; this is only a floor */ }
    }

    await refresh();
    poll = window.setInterval(() => void refresh(), POLL_MS);
  },

  unmount(): void {
    stopPoll();
  },
};

function stopPoll(): void {
  if (poll !== null) {
    window.clearInterval(poll);
    poll = null;
  }
}

function plate(label: string, onClick: () => void, quiet = false): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = quiet ? "plate plate--quiet" : "plate";
  b.dataset["label"] = label;
  const face = document.createElement("span");
  face.className = "plate__face";
  face.setAttribute("aria-hidden", "true");
  const l = document.createElement("span");
  l.className = "plate__label";
  l.textContent = label;
  b.append(face, l);
  b.addEventListener("click", onClick);
  return b;
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
